import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Errors the browser hits are reported to the API (`POST /telemetry/client-error`),
 * where they are logged and counted: a screen that crashed, a promise nobody
 * caught. A handful per page load at most, each distinct message once — a
 * render loop must not become a request loop. Reporting never throws.
 */
const APP = 'partner-portal'
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'
const LIMIT = 5
const sent = new Set<string>()

export function reportClientError(kind: 'error' | 'unhandledrejection' | 'render', error: unknown, extra?: string): void {
  try {
    const message = (error instanceof Error ? error.message : String(error ?? 'Unknown error')).slice(0, 500)
    if (!message || sent.has(message) || sent.size >= LIMIT) return
    sent.add(message)
    const stack = [error instanceof Error ? error.stack : null, extra].filter(Boolean).join('\n').slice(0, 4000)
    void fetch(`${BASE_URL}/telemetry/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ app: APP, kind, message, stack: stack || undefined, url: window.location.pathname }),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    /* reporting an error must not cause one */
  }
}

/** Uncaught errors and unhandled promise rejections, for the life of the page. */
export function initErrorReporting(): void {
  window.addEventListener('error', (event) => reportClientError('error', event.error ?? event.message))
  window.addEventListener('unhandledrejection', (event) => reportClientError('unhandledrejection', event.reason))
}

/** A screen that throws while rendering shows this instead of a blank page, and says so to the API. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError('render', error, info.componentStack ?? undefined)
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <div role="alert" style={{ maxWidth: 440, margin: '18vh auto', padding: '0 16px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600 }}>Something went wrong on this screen</h1>
        <p style={{ marginTop: 8, fontSize: 14, opacity: 0.75 }}>It has been reported. Reloading usually clears it.</p>
        <button type="button" onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, border: '1px solid currentColor', background: 'transparent', cursor: 'pointer' }}>
          Reload
        </button>
      </div>
    )
  }
}
